'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { Autoplay, EffectFade } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css/autoplay'
import 'swiper/css/effect-fade'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

// Define the structure for our slide items
type SlideItem = {
  type: 'image' | 'video'
  src: string
  srcMobile: string
  alt?: string
}

export function SwiperCarousel({ slides }: Readonly<{ slides: SlideItem[] }>) {
  // __STATE's
  const [swiper, setSwiper] = useState<SwiperType | null>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])

  // __FUNCTION's
  const handleVideoEnded = useCallback(
    (video: HTMLVideoElement) => {
      if (swiper) swiper.slideNext()
    },
    [swiper]
  )

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    const video = videoRefs.current?.[swiper.realIndex]
    if (video) {
      setTimeout(swiper.autoplay.stop, 5)
      video.currentTime = 0
      video.play().catch((error) => console.error('Error playing video:', error))
    } else {
      swiper.autoplay.start()
    }
  }, [])

  const preloadNextVideo = useCallback((swiper: SwiperType) => {
    const nextIndex = (swiper.realIndex + 1) % slides.length
    if (nextIndex === swiper.realIndex) return void 0

    const nextSlide = slides[nextIndex]
    if (nextSlide?.type === 'video') {
      const video = videoRefs.current?.[nextIndex]
      if (video) video.load()
    }
  }, [])

  // __EFFECT's
  useEffect(() => {
    const tryPlay = () => {
      videoRefs.current?.forEach((video) => {
        if (video) {
          video.play().catch((err) => console.error('Play failed:', err))
        }
      })

      window.removeEventListener('touchstart', tryPlay)
      window.removeEventListener('click', tryPlay)
    }

    window.addEventListener('touchstart', tryPlay, { once: true })
    window.addEventListener('click', tryPlay, { once: true })
  }, [])

  return (
    <div className='absolute size-full max-w-2k'>
      <Swiper
        onSwiper={setSwiper}
        modules={[EffectFade, Autoplay]}
        className='h-full'
        effect='fade'
        fadeEffect={{ crossFade: true }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false
        }}
        speed={800}
        loop={true}
        onSlideChange={handleSlideChange}
        onSlideChangeTransitionEnd={preloadNextVideo}>
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            {slide?.type === 'image' ? (
              <picture>
                <source srcSet={slide.srcMobile} media='(max-width:640px)' />
                <img
                  src={slide.src}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  alt='Swiper Carousel'
                />
              </picture>
            ) : (
              <video
                ref={(el) => {
                  videoRefs.current[index] = el
                }}
                key={index}
                className='object-cover object-center'
                muted
                autoPlay
                playsInline
                preload='metadata'
                poster='/static/images/poster.webp'
                onEnded={({ target }) => handleVideoEnded(target as HTMLVideoElement)}
                aria-label={`Video slide ${index + 1}`}>
                <source src={slide.srcMobile} media='(max-width: 640px)' />
                <source src={slide.src} />
              </video>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}
